import React, { useState, useEffect } from 'react';
import { Box, Typography, IconButton, Tooltip, Paper, TableContainer, Table, TableHead, TableRow, TableCell, TableBody, ToggleButton, ToggleButtonGroup, TablePagination } from '@mui/material';
import { RestartAlt as RestartAltIcon } from '@mui/icons-material';
import {
  ComposedChart,
  Bar,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import { useChartConfig } from './ChartConfig';

const ViolationsCharts = ({ 
  violationsData, 
  onSortViolationsNotCompliant,
  violationsNotCompliantSortField, 
  violationsNotCompliantSortDirection,
  onResetViolationsNotCompliant,
  violationsFilter,
  setViolationsFilter,
  resetViolationsFilter,
  hiddenViolationsLines,
  setHiddenViolationsLines,
  resetHiddenViolationsLines,
  hiddenReinspectionLines,
  setHiddenReinspectionLines,
  resetHiddenReinspectionLines
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const chartConfig = useChartConfig();

  // Pagination handlers
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Check if violations have data for each filter
  const violationsDataUnresolved = violationsData.filter(record => {
    const reinspectionColumn = record.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
    // Include if no reinspection data (not yet reinspected) or if reinspection shows non-compliance
    if (!reinspectionColumn || !record[reinspectionColumn] || record[reinspectionColumn].toString().trim() === '' || record[reinspectionColumn].toString().trim() === '--') {
      return true; // Not yet reinspected
    }
    // Check if reinspection shows non-compliance (N or NO)
    const reinspectionValue = record[reinspectionColumn].toString().toUpperCase();
    return reinspectionValue === 'N' || reinspectionValue === 'NO';
  });
  const violationsDataAll = violationsData; // All violations
  const violationsDataMajor = violationsData.filter(record => {
    const majorViolationColumn = record.major_violation_indicator !== undefined ? 'major_violation_indicator' : null;
    return majorViolationColumn && record[majorViolationColumn] && 
           record[majorViolationColumn].toString().toUpperCase() === 'Y';
  });
  const hasViolationsUnresolved = violationsDataUnresolved.length > 0;
  const hasViolationsAll = violationsDataAll.length > 0;
  const hasViolationsMajor = violationsDataMajor.length > 0;
  const hasAnyViolations = hasViolationsUnresolved || hasViolationsAll || hasViolationsMajor;

  // Ensure the current filter is valid (has data)
  // If current filter has no data, switch to the first available one
  const validViolationsFilter = (() => {
    if (violationsFilter === 'unresolved' && !hasViolationsUnresolved) {
      if (hasViolationsAll) return 'all';
      if (hasViolationsMajor) return 'major';
      return violationsFilter;
    }
    if (violationsFilter === 'all' && !hasViolationsAll) {
      if (hasViolationsUnresolved) return 'unresolved';
      if (hasViolationsMajor) return 'major';
      return violationsFilter;
    }
    if (violationsFilter === 'major' && !hasViolationsMajor) {
      if (hasViolationsUnresolved) return 'unresolved';
      if (hasViolationsAll) return 'all';
      return violationsFilter;
    }
    return violationsFilter;
  })();

  // Update filter state if it's invalid
  useEffect(() => {
    if (validViolationsFilter !== violationsFilter) {
      setViolationsFilter(validViolationsFilter);
    }
  }, [validViolationsFilter, violationsFilter, setViolationsFilter]);

  return (
    <Box sx={{ mt: 2 }}>
      {/* Chart 1: Violations Over Time */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Rule Violations Over Time
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenViolationsLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <ComposedChart 
            data={(() => {
              // Group data by month and calculate monthly counts
              const monthlyData = {};
              
              violationsData.forEach((record, index) => {
                const dateColumn = record.violation_discovery_date !== undefined ? 'violation_discovery_date' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  
                  if (!isNaN(date.getTime())) {
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        totalViolations: 0,
                        majorViolations: 0
                      };
                    }
                    
                    monthlyData[monthKey].totalViolations++;
                    
                    // Check for major violations
                    const majorViolationsColumn = record.major_violation_indicator !== undefined ? 'major_violation_indicator' : null;
                    
                    if (majorViolationsColumn && record[majorViolationsColumn] && 
                        record[majorViolationsColumn].toString().toUpperCase() === 'Y') {
                      monthlyData[monthKey].majorViolations++;
                    }
                  }
                }
              });
              
              // Convert to array and sort by month
              return Object.values(monthlyData)
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" tick={{ fill: '#2196f3' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#e91e63' }} />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'Total Violations') {
                  return [`${value.toLocaleString()}`, 'Total'];
                } else if (name === 'Major Violations') {
                  return [`${value.toLocaleString()}`, 'Major'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenViolationsLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            <Bar 
              yAxisId="left"
              dataKey="totalViolations" 
              fill="#2196f3" 
              name="Total Violations"
              hide={hiddenViolationsLines.has('totalViolations')}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="majorViolations" 
              stroke="#e91e63" 
              strokeWidth={2}
              name="Major Violations"
              dot={{ fill: '#e91e63', strokeWidth: 2, r: 4 }}
              hide={hiddenViolationsLines.has('majorViolations')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Chart 2: Reinspection Compliance */}
      <Paper elevation={0} sx={{ p: 2, mb: 3, border: 1, borderColor: 'divider' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Reinspection Compliance Trends
          </Typography>
          <Tooltip title="Reset Graph" arrow>
            <IconButton
              size="small"
              color="primary"
              onClick={() => setHiddenReinspectionLines(new Set())}
            >
              <RestartAltIcon />
            </IconButton>
          </Tooltip>
        </Box>
        <ResponsiveContainer {...chartConfig.containerProps}>
          <ComposedChart 
            data={(() => {
              // Group data by month and calculate monthly counts
              const monthlyData = {};
              
              violationsData.forEach(record => {
                const dateColumn = record.violation_discovery_date !== undefined ? 'violation_discovery_date' : null;
                
                if (dateColumn && record[dateColumn]) {
                  const date = new Date(record[dateColumn]);
                  if (!isNaN(date.getTime())) {
                    const monthKey = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
                    
                    if (!monthlyData[monthKey]) {
                      monthlyData[monthKey] = {
                        month: monthKey,
                        reinspections: 0,
                        compliantReinspections: 0
                      };
                    }
                    
                    // Check for reinspections
                    const reinspectionColumn = record.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
                    
                    if (reinspectionColumn && record[reinspectionColumn] && 
                        record[reinspectionColumn].toString().trim() !== '' && 
                        record[reinspectionColumn].toString().trim() !== '--') {
                      monthlyData[monthKey].reinspections++;
                      
                      // Check if compliant
                      if (record[reinspectionColumn].toString().toUpperCase() === 'Y') {
                        monthlyData[monthKey].compliantReinspections++;
                      }
                    }
                  }
                }
              });
              
              // Convert to array and calculate compliance rate
              return Object.values(monthlyData)
                .map(data => ({
                  month: data.month,
                  reinspections: data.reinspections,
                  compliantReinspections: data.compliantReinspections,
                  complianceRate: data.reinspections > 0 ? (data.compliantReinspections / data.reinspections) * 100 : 0
                }))
                .sort((a, b) => a.month.localeCompare(b.month));
            })()}
            margin={chartConfig.margins}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              angle={-45}
              textAnchor="end"
              height={60}
            />
            <YAxis yAxisId="left" tick={{ fill: '#2196f3' }} />
            <YAxis yAxisId="right" orientation="right" tick={{ fill: '#ff9800' }} />
            <RechartsTooltip 
              formatter={(value, name) => {
                if (name === 'Reinspections') {
                  return [`${value.toLocaleString()}`, 'Count'];
                } else if (name === 'Compliance Rate (%)') {
                  return [`${value.toFixed(1)}%`, 'Rate'];
                }
                return [value, name];
              }}
              labelFormatter={(label) => `Month: ${label}`}
            />
            <Legend 
              onClick={(entry) => {
                const lineType = entry.dataKey;
                setHiddenReinspectionLines(prev => {
                  const newSet = new Set(prev);
                  if (newSet.has(lineType)) {
                    newSet.delete(lineType);
                  } else {
                    newSet.add(lineType);
                  }
                  return newSet;
                });
              }}
            />
            <Bar 
              yAxisId="left"
              dataKey="reinspections" 
              fill="#2196f3" 
              name="Reinspections"
              hide={hiddenReinspectionLines.has('reinspections')}
            />
            <Line 
              yAxisId="right"
              type="monotone" 
              dataKey="complianceRate" 
              stroke="#ff9800" 
              strokeWidth={2}
              name="Compliance Rate (%)"
              dot={{ fill: '#ff9800', strokeWidth: 2, r: 4 }}
              hide={hiddenReinspectionLines.has('complianceRate')}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Paper>

      {/* Violations Not in Compliance on Reinspection or Not Yet Reinspected - Only show if there's data for at least one filter */}
      {hasAnyViolations && (
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 3 }, mb: 3, border: 1, borderColor: 'divider' }} data-table-title={
          validViolationsFilter === 'all' ? 'All Violations' : 
          validViolationsFilter === 'major' ? 'All Major Violations' : 
        'Violations Not in Compliance on Reinspection or Not Yet Reinspected'
      } data-table-type="main">
        <Box sx={{ mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              {validViolationsFilter === 'all' ? 'All Violations' : 
               validViolationsFilter === 'major' ? 'All Major Violations' : 
               'Violations Not in Compliance on Reinspection or Not Yet Reinspected'}
            </Typography>
            <Tooltip title="Reset Table" arrow>
              <IconButton
                size="small"
                color="primary"
                onClick={() => {
                  onResetViolationsNotCompliant();
                  resetViolationsFilter();
                  resetHiddenViolationsLines();
                  resetHiddenReinspectionLines();
                }}
              >
                <RestartAltIcon />
              </IconButton>
            </Tooltip>
          </Box>
          {/* Only show toggle if at least two options are available */}
          {((hasViolationsUnresolved ? 1 : 0) + (hasViolationsAll ? 1 : 0) + (hasViolationsMajor ? 1 : 0)) >= 2 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mb: 1 }}>
            <ToggleButtonGroup
                value={validViolationsFilter}
              exclusive
              onChange={(event, newFilter) => {
                if (newFilter !== null) {
                  setViolationsFilter(newFilter);
                }
              }}
              size="small"
              sx={{
                '& .MuiToggleButton-root': {
                  '&.Mui-selected': {
                    backgroundColor: 'primary.main',
                    color: 'primary.contrastText',
                    '&:hover': {
                      backgroundColor: 'primary.dark',
                    }
                  }
                }
              }}
            >
                {hasViolationsUnresolved && (
              <ToggleButton value="unresolved">
                Unresolved
              </ToggleButton>
                )}
                {hasViolationsAll && (
              <ToggleButton value="all">
                All
              </ToggleButton>
                )}
                {hasViolationsMajor && (
                <ToggleButton value="major">
                  All Major
                </ToggleButton>
              )}
            </ToggleButtonGroup>
          </Box>
          )}
        </Box>
        <TableContainer component={Paper} sx={{ mt: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortViolationsNotCompliant('lease_facility_name')}
                >
                  Lease Facility Name
                  {violationsNotCompliantSortField === 'lease_facility_name' && (
                    <span style={{ marginLeft: 8 }}>
                      {violationsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortViolationsNotCompliant('violation_discovery_date')}
                >
                  Violation Discovery Date
                  {violationsNotCompliantSortField === 'violation_discovery_date' && (
                    <span style={{ marginLeft: 8 }}>
                      {violationsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortViolationsNotCompliant('violated_rule_description')}
                >
                  Violated Rule Description
                  {violationsNotCompliantSortField === 'violated_rule_description' && (
                    <span style={{ marginLeft: 8 }}>
                      {violationsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortViolationsNotCompliant('major_violation_indicator')}
                >
                  Major Violation Indicator
                  {violationsNotCompliantSortField === 'major_violation_indicator' && (
                    <span style={{ marginLeft: 8 }}>
                      {violationsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
                <TableCell 
                  sx={{ fontWeight: 'bold', cursor: 'pointer', userSelect: 'none' }}
                  onClick={() => onSortViolationsNotCompliant('compliant_on_reinspection')}
                >
                  Compliant on Reinspection
                  {violationsNotCompliantSortField === 'compliant_on_reinspection' && (
                    <span style={{ marginLeft: 8 }}>
                      {violationsNotCompliantSortDirection === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(() => {
                // Filter violations based on user selection
                const filteredViolations = violationsData.filter(record => {
                  if (validViolationsFilter === 'all') {
                    return true; // Show all violations
                  }
                  
                  if (validViolationsFilter === 'major') {
                    // Filter for major violations only
                    const majorViolationColumn = record.major_violation_indicator !== undefined ? 'major_violation_indicator' : null;
                    return majorViolationColumn && record[majorViolationColumn] && 
                           record[majorViolationColumn].toString().toUpperCase() === 'Y';
                  }
                  
                  // Filter for violations not in compliance on reinspection or not yet reinspected
                  const reinspectionColumn = record.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
                  
                  // Include if no reinspection data (not yet reinspected) or if reinspection shows non-compliance
                  if (!reinspectionColumn || !record[reinspectionColumn] || record[reinspectionColumn].toString().trim() === '' || record[reinspectionColumn].toString().trim() === '--') {
                    return true; // Not yet reinspected
                  }
                  
                  // Check if reinspection shows non-compliance (N or NO)
                  const reinspectionValue = record[reinspectionColumn].toString().toUpperCase();
                  return reinspectionValue === 'N' || reinspectionValue === 'NO';
                });

                // Sort the filtered violations
                filteredViolations.sort((a, b) => {
                  let aValue, bValue;
                  
                  switch (violationsNotCompliantSortField) {
                    case 'lease_facility_name':
                      aValue = (a.lease_facility_name || '').toLowerCase();
                      bValue = (b.lease_facility_name || '').toLowerCase();
                      break;
                    case 'violation_discovery_date':
                      aValue = new Date(a.violation_discovery_date || '');
                      bValue = new Date(b.violation_discovery_date || '');
                      break;
                    case 'violated_rule_description':
                      aValue = (a.violated_rule_description || '').toLowerCase();
                      bValue = (b.violated_rule_description || '').toLowerCase();
                      break;
                    case 'major_violation_indicator':
                      aValue = (a.major_violation_indicator || '').toLowerCase();
                      bValue = (b.major_violation_indicator || '').toLowerCase();
                      break;
                    case 'compliant_on_reinspection':
                      aValue = (a.compliant_on_reinspection || '').toLowerCase();
                      bValue = (b.compliant_on_reinspection || '').toLowerCase();
                      break;
                    default:
                      aValue = new Date(a.violation_discovery_date || '');
                      bValue = new Date(b.violation_discovery_date || '');
                  }
                  
                  if (violationsNotCompliantSortDirection === 'asc') {
                    return aValue > bValue ? 1 : -1;
                  } else {
                    return aValue < bValue ? 1 : -1;
                  }
                });

                // Apply pagination
                const startIndex = page * rowsPerPage;
                const endIndex = startIndex + rowsPerPage;
                const paginatedData = filteredViolations.slice(startIndex, endIndex);

                return paginatedData.map((record, index) => (
                  <TableRow key={startIndex + index}>
                    <TableCell>
                      {record.lease_facility_name || null}
                    </TableCell>
                    <TableCell>
                      {record.violation_discovery_date || null}
                    </TableCell>
                    <TableCell>
                      {record.violated_rule_description || null}
                    </TableCell>
                    <TableCell>
                      {record.major_violation_indicator || null}
                    </TableCell>
                    <TableCell>
                      {record.compliant_on_reinspection || null}
                    </TableCell>
                  </TableRow>
                ));
              })()}
            </TableBody>
          </Table>
        </TableContainer>
        
        {/* Pagination */}
        {(() => {
          // Calculate total rows for pagination
          const filteredViolations = violationsData.filter(record => {
            if (validViolationsFilter === 'all') {
              return true;
            }
            if (validViolationsFilter === 'major') {
              const majorViolationColumn = record.major_violation_indicator !== undefined ? 'major_violation_indicator' : null;
              return majorViolationColumn && record[majorViolationColumn] && 
                     record[majorViolationColumn].toString().toUpperCase() === 'Y';
            }
            const reinspectionColumn = record.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
            if (!reinspectionColumn || !record[reinspectionColumn] || record[reinspectionColumn].toString().trim() === '' || record[reinspectionColumn].toString().trim() === '--') {
              return true;
            }
            const reinspectionValue = record[reinspectionColumn].toString().toUpperCase();
            return reinspectionValue === 'N' || reinspectionValue === 'NO';
          });
          
          return (
            <TablePagination
              rowsPerPageOptions={[5, 10, 25, 50]}
              component="div"
              count={filteredViolations.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelRowsPerPage="Rows:"
              labelDisplayedRows={({ from, to, count }) => 
                `${from}-${to} of ${count !== -1 ? count : `more than ${to}`}`
              }
              sx={{
                '& .MuiTablePagination-toolbar': {
                  justifyContent: 'center',
                },
                '& .MuiTablePagination-spacer': {
                  display: 'none',
                },
              }}
            />
          );
        })()}
      </Paper>
      )}
    </Box>
  );
};

export default ViolationsCharts;